import { Grid2 as Grid, Pagination, Stack } from '@mui/material'
import PhotoTextBlock, {
  PhotoTextBlockProps,
} from 'App/Epi/Blocks/PhotoTextBlock/PhotoTextBlock'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CardItem, CardProps } from '../Card/Card'
import FilterSelect from '../Select'

export interface ArticlesCardGridProps {
  firstArticle?: PhotoTextBlockProps
  articles: CardProps[]
  paginationCallback: () => void
}

const ArticlesCardGrid = ({
  firstArticle,
  articles,
  paginationCallback,
}: ArticlesCardGridProps) => {
  const [page, setPage] = useState(1)
  const PAGE_SIZE = 12

  const { t } = useTranslation()
  const handlePaginationClick = (value: number) => {
    setPage(value)
    paginationCallback()
    window.scrollTo(0, 0)
  }

  const [selectedYears, setSelectedYears] = useState<string[]>([])
  const [selectedCategories, setSelectedCategories] = useState<string[]>([])

  const uniqueYears = useMemo(() => {
    const validYears = articles
      .map((article) => new Date(article.publishDate))
      .filter((date) => !isNaN(date.getTime()))
      .map((date) => date.getUTCFullYear().toString())

    return Array.from(new Set(validYears))
  }, [articles])

  const uniqueCategories: string[] = useMemo(() => {
    return [
      ...new Set(
        articles
          .map((article) => article.category)
          .filter((category) => Boolean(category))
      ),
    ]
  }, [articles])

  const filteredArticles = useMemo(() => {
    return articles.filter((article) => {
      const year = new Date(article.publishDate).getUTCFullYear().toString()
      const category = article.category
      const yearMatch =
        selectedYears.length === 0 || (year && selectedYears.includes(year))

      const categoryMatch =
        selectedCategories.length === 0 ||
        (category && selectedCategories.includes(category))

      return yearMatch && categoryMatch
    })
  }, [articles, selectedYears, selectedCategories])

  const pagesCount = Math.ceil(filteredArticles.length / PAGE_SIZE)

  return (
    <Stack alignItems={'flex-start'}>
      {page === 1 && firstArticle && (
        <PhotoTextBlock content={firstArticle.content} />
      )}
      <Stack
        direction="row"
        spacing={1}
        justifyContent="space-between"
        alignItems="center"
        width="100%"
        sx={{ mb: 2 }}
      >
        <Stack direction="row" spacing={1}>
          <FilterSelect
            options={uniqueCategories}
            selectedValues={selectedCategories}
            onChange={(value) => setSelectedCategories(value)}
            label={t('translation:filter_label.category')}
          />

          <FilterSelect
            label={t('translation:filter_label.year')}
            options={uniqueYears}
            selectedValues={selectedYears}
            onChange={(value) => setSelectedYears(value)}
          />
        </Stack>
      </Stack>
      {filteredArticles.length > 0 && (
        <Grid
          container
          spacing={{ xs: 2, md: 3 }}
          mb={3}
          data-testid="articles-card-grid"
        >
          {filteredArticles
            .slice((page - 1) * PAGE_SIZE, (page - 1) * PAGE_SIZE + PAGE_SIZE)
            .map((article) => (
              <Grid
                key={`article-item-${article.link.url}`}
                size={{ xs: 12, md: 4 }}
                sx={{
                  minWidth: { md: 363 },
                }}
              >
                <CardItem
                  {...article}
                  useColor={false}
                  isEditorialCardBlock={false}
                  fullHeight
                />
              </Grid>
            ))}
        </Grid>
      )}
      {pagesCount > 1 && (
        <Pagination
          data-testid="pagination-container"
          count={pagesCount}
          page={page}
          onChange={(_, v) => {
            handlePaginationClick(v)
          }}
        />
      )}
    </Stack>
  )
}

export default ArticlesCardGrid
